/**
 * Per-screen brand-column content for S01, S02, S03, S05.
 *
 * Each of these four screens has the same brand-panel shape — eyebrow / title
 * / subtitle / source + an InsightPanel (4 KPIs) + a StoryList (3 items) —
 * with only the copy differing. Earlier we had four files (`sign-in-brand.tsx`,
 * `otp-brand.tsx`, `workspace-brand.tsx`, `done-brand.tsx`) that each
 * re-declared the same JSX skeleton plus three inline SVG icons; this file
 * collapses them into one config + one renderer.
 *
 * S04 (Tally) keeps its own `brand/tally-brand.tsx` because it carries a
 * screen-specific `<MigrationPreview>` card that no other screen uses.
 *
 * Icons come from `lucide-react` — the project standard.
 */
import { ArrowLeftRight, Building2, Check, Clock, Inbox, List, Phone, Shield } from "lucide-react";
import { MetaPill } from "@flint/ui";
import { BrandPanel, InsightPanel, InsightKpi, StoryList } from "./index.ts";

export type BrandStep = "signIn" | "otp" | "workspace" | "done";

interface BrandConfig {
  eyebrow: string;
  title: string;
  subtitle: string;
  source: string;
  insight: {
    title: string;
    caption: string;
    badge: { text: string; tone?: "ok" };
    kpis: ReadonlyArray<{ label: string; value: string; detail: string }>;
  };
  stories: ReadonlyArray<{
    icon: React.ReactNode;
    title: string;
    description: string;
  }>;
}

const ICON = { size: 14, strokeWidth: 2 } as const;

const BRAND_CONTENT: Record<BrandStep, BrandConfig> = {
  signIn: {
    eyebrow: "Identity first",
    title:
      "The login step should not force the user to decide whether they are signing in or signing up.",
    subtitle:
      "One phone-first entry keeps the product legible. After OTP, Flint can detect whether this number already belongs to a company, has an invite waiting, or needs a new workspace.",
    source: "Founder entry point · phone OTP first · workspace and trial follow",
    insight: {
      title: "What happens next",
      caption: "Routing after phone verification",
      badge: { text: "Live", tone: "ok" },
      kpis: [
        { label: "Existing", value: "Open", detail: "Membership found" },
        { label: "New", value: "Create", detail: "Workspace setup begins" },
        { label: "Invite", value: "Join", detail: "Operator accepts company invite" },
        { label: "Trial", value: "14d", detail: "Starts after workspace setup" },
      ],
    },
    stories: [
      {
        icon: <Shield {...ICON} />,
        title: "One entry, three outcomes",
        description:
          "The user should never choose between sign-in and sign-up before the system has checked what this phone number already belongs to.",
      },
      {
        icon: <List {...ICON} />,
        title: "Workspace creation is the real signup",
        description:
          "OTP proves the person. Founder name, company name, and work email establish the business workspace that billing and audit attach to.",
      },
      {
        icon: <ArrowLeftRight {...ICON} />,
        title: "Trial explains commitment without blocking trust",
        description:
          "The free trial starts only after the workspace exists, so the user sees real product value before billing activation is asked for.",
      },
    ],
  },

  otp: {
    eyebrow: "Trust, quickly",
    title: "Security should read as calm control, not friction.",
    subtitle:
      "The founder shouldn't feel like they're entering an admin workflow. This step is short, familiar, and sets up stronger verification moments later without making the first session heavy.",
    source:
      "OTP is the only login method in V1 · email supports billing, recovery, and delivery only",
    insight: {
      title: "Verification policy",
      caption: "How Flint treats identity in V1",
      badge: { text: "Active", tone: "ok" },
      kpis: [
        { label: "Session", value: "30d", detail: "Sliding session window" },
        { label: "Step-up", value: "OTP", detail: "For high-stakes actions" },
        { label: "Channel", value: "WA", detail: "Read + approve only" },
        { label: "Recovery", value: "24h", detail: "Cooling-off after reset" },
      ],
    },
    stories: [
      {
        icon: <Shield {...ICON} />,
        title: "Single-use by default",
        description:
          "Codes expire quickly and don't leave behind a reusable password surface for the founder to manage.",
      },
      {
        icon: <Clock {...ICON} />,
        title: "Fast now, strict when it matters",
        description:
          "Payroll, user changes, and bank detail edits can ask for a fresh code later without burdening every interaction.",
      },
      {
        icon: <Phone {...ICON} />,
        title: "Works the way the user expects",
        description:
          "OTP via phone matches how Indian founders already verify across WhatsApp, banking, and payments.",
      },
    ],
  },

  workspace: {
    eyebrow: "Workspace before workflow",
    title:
      "The first business form should establish who the company is, not dump the founder into tax fields alone.",
    subtitle:
      "This step creates the workspace that identity, billing, and audit attach to. GSTIN improves the setup, but the product should not pretend GST lookup alone is the signup.",
    source: "Workspace contract · founder confirms · GST enriches when available",
    insight: {
      title: "What this step locks in",
      caption: "The minimum viable company contract",
      badge: { text: "Required" },
      kpis: [
        { label: "Identity", value: "Founder", detail: "Named owner of the workspace" },
        { label: "Company", value: "Workspace", detail: "Company and billing context" },
        { label: "Tax", value: "GST", detail: "Auto-filled when available" },
        { label: "Trial", value: "14d", detail: "Commitment without upfront billing" },
      ],
    },
    stories: [
      {
        icon: <Building2 {...ICON} />,
        title: "Company name is not optional theater",
        description:
          "The system needs an explicit business identity even when the founder does not have GSTIN handy at this moment.",
      },
      {
        icon: <List {...ICON} />,
        title: "GSTIN improves setup instead of blocking it",
        description:
          "Autofill is valuable, but it should support workspace creation rather than standing in for it.",
      },
      {
        icon: <Check {...ICON} />,
        title: "The trial starts once the workspace exists",
        description:
          "That keeps the product commercially serious without asking for billing before the founder has seen any value.",
      },
    ],
  },

  done: {
    eyebrow: "Commercially serious",
    title:
      "The user should leave onboarding knowing both that the workspace is ready and that the trial clock is real.",
    subtitle:
      "This handoff makes the product feel open enough to explore and disciplined enough to become paid software. The next screens should preserve both signals.",
    source: "Workspace created · trial active · founder can enter the real shell",
    insight: {
      title: "What's locked in now",
      caption: "The contract after onboarding finishes",
      badge: { text: "Done", tone: "ok" },
      kpis: [
        { label: "Identity", value: "Verified", detail: "Workspace phone is trusted" },
        { label: "Workspace", value: "Created", detail: "Company and owner are named" },
        { label: "Trial", value: "14d", detail: "Billing activation can wait" },
        { label: "Next", value: "Operate", detail: "Dashboard and first workflows" },
      ],
    },
    stories: [
      {
        icon: <List {...ICON} />,
        title: "Company setup is explicit now",
        description:
          "The flow no longer jumps from OTP to GST and leaves the company identity implied rather than captured.",
      },
      {
        icon: <Inbox {...ICON} />,
        title: "Trial state is visible, not buried",
        description:
          "The user can see the commercial status immediately instead of discovering billing only after investing effort deeper in the app.",
      },
      {
        icon: <Check {...ICON} />,
        title: "Still aligned with the product idea",
        description:
          'The flow still ends at "ready to move," but it now does so with a proper workspace contract and a real trial.',
      },
    ],
  },
};

export interface BrandForProps {
  step: BrandStep;
}

export function BrandFor({ step }: BrandForProps) {
  const c = BRAND_CONTENT[step];
  return (
    <BrandPanel eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} source={c.source}>
      <InsightPanel
        title={c.insight.title}
        caption={c.insight.caption}
        badge={<MetaPill tone={c.insight.badge.tone}>{c.insight.badge.text}</MetaPill>}
      >
        {c.insight.kpis.map((k) => (
          <InsightKpi key={k.label} label={k.label} value={k.value} detail={k.detail} />
        ))}
      </InsightPanel>
      <StoryList items={c.stories} />
    </BrandPanel>
  );
}
